import { StyleSheet } from "@react-pdf/renderer";

// Options mirror ResumeDocxTemplateOptions in docx-builder.ts. Spacing values
// reuse the DOCX twips units and are converted to points (20 twips = 1pt).
export type ResumePdfTemplateOptions = {
  accentColor: string; // hex color like "#0ea5e9"
  headingFont: string; // DOCX font family name, approximated with a standard PDF font
  bodyFont: string;
  headerAlignment?: "center" | "left";
  headingTransform?: "uppercase" | "title" | "none";
  headingUnderline?: boolean;
  summaryAlignment?: "center" | "left" | "justify";
  nameFontSize?: number; // pt
  includeOrganizations?: boolean;
  includeHighlights?: boolean;
  contactSeparator?: string;
  bulletSpacing?: number; // twips after each bullet, same as the DOCX option
};

export type ResolvedPdfTemplateOptions = Required<ResumePdfTemplateOptions>;

const defaultOptions = {
  headerAlignment: "center",
  headingTransform: "uppercase",
  summaryAlignment: "justify",
  headingUnderline: false,
  includeOrganizations: true,
  includeHighlights: true,
  contactSeparator: " · ",
  nameFontSize: 26,
  bulletSpacing: 80,
} as const;

export const INLINE_SEPARATOR = " · ";
export const MUTED_COLOR = "#6B7280";
export const BODY_COLOR = "#1F2933";

// Page geometry mirrors the DOCX section in docx-builder.ts, expressed there in
// twips (20 twips = 1pt). Matching it keeps the PDF preview paginating and
// wrapping like the file the user actually downloads.
const twipsToPoints = (twips: number) => twips / 20;

const PAGE_WIDTH_TWIPS = 12240;
const PAGE_HEIGHT_TWIPS = 15840;
const PAGE_MARGIN_TWIPS = 720;

/** 612 x 792 pt (US Letter), same as the DOCX page size. */
export const PAGE_SIZE: [number, number] = [
  twipsToPoints(PAGE_WIDTH_TWIPS),
  twipsToPoints(PAGE_HEIGHT_TWIPS),
];
export const PAGE_MARGIN = twipsToPoints(PAGE_MARGIN_TWIPS);

const FALLBACK_COLOR = "#111111";

// Accepts 3- or 6-digit hex with or without a leading "#". Anything else falls
// back rather than producing a malformed value the PDF renderer would reject.
const sanitizeColor = (value: string) => {
  const hex = value.trim().replace(/^#/, "").toUpperCase();
  return /^([0-9A-F]{3}|[0-9A-F]{6})$/.test(hex) ? `#${hex}` : FALLBACK_COLOR;
};

// @react-pdf/renderer supports the standard 14 PDF fonts out of the box.
// DOCX font families are approximated with serif (Times) or sans (Helvetica).
type FontVariant = "regular" | "bold" | "italic" | "boldItalic";

const SERIF_VARIANTS: Record<FontVariant, string> = {
  regular: "Times-Roman",
  bold: "Times-Bold",
  italic: "Times-Italic",
  boldItalic: "Times-BoldItalic",
};

const SANS_VARIANTS: Record<FontVariant, string> = {
  regular: "Helvetica",
  bold: "Helvetica-Bold",
  italic: "Helvetica-Oblique",
  boldItalic: "Helvetica-BoldOblique",
};

const SERIF_FAMILIES = new Set([
  "times new roman",
  "times",
  "georgia",
  "cambria",
  "serif",
]);

export const mapFontVariant = (
  fontName: string,
  variant: FontVariant = "regular",
): string => {
  const variants = SERIF_FAMILIES.has(fontName.trim().toLowerCase())
    ? SERIF_VARIANTS
    : SANS_VARIANTS;
  return variants[variant];
};

export const resolvePdfTemplateOptions = (
  options: ResumePdfTemplateOptions,
): ResolvedPdfTemplateOptions => ({
  ...defaultOptions,
  ...options,
  accentColor: sanitizeColor(options.accentColor),
});

export const createPdfStyles = (theme: ResolvedPdfTemplateOptions) => {
  const headingBold = mapFontVariant(theme.headingFont, "bold");
  const bodyRegular = mapFontVariant(theme.bodyFont);
  const bodyBold = mapFontVariant(theme.bodyFont, "bold");
  const bodyItalic = mapFontVariant(theme.bodyFont, "italic");

  return StyleSheet.create({
    page: {
      padding: PAGE_MARGIN,
      fontFamily: bodyRegular,
      fontSize: 10.5,
      lineHeight: 1.4,
      color: BODY_COLOR,
    },
    headerName: {
      fontFamily: headingBold,
      fontSize: theme.nameFontSize,
      // The page lineHeight is tuned for 10.5pt body text and leaves the much
      // larger name glyphs overlapping the contact line, so set it explicitly.
      lineHeight: 1.25,
      color: theme.accentColor,
      textAlign: theme.headerAlignment,
      marginBottom: 3, // 60 twips
    },
    headerContact: {
      color: MUTED_COLOR,
      textAlign: theme.headerAlignment,
      marginBottom: 2, // 40 twips
    },
    headerLinks: {
      color: MUTED_COLOR,
      textAlign: theme.headerAlignment,
      marginBottom: 3, // 60 twips
    },
    sectionHeading: {
      fontFamily: headingBold,
      fontSize: 12,
      color: theme.accentColor,
      marginTop: 12, // 240 twips
      marginBottom: 4, // 80 twips
      ...(theme.headingUnderline
        ? {
            borderBottomWidth: 1,
            borderBottomColor: theme.accentColor,
            paddingBottom: 2,
          }
        : {}),
    },
    summaryText: {
      textAlign: theme.summaryAlignment,
      marginBottom: 5, // 100 twips
    },
    entryHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginTop: 7, // 140 twips
      marginBottom: 1, // 20 twips
    },
    entryTitle: {
      fontFamily: headingBold,
      color: theme.accentColor,
      flex: 1,
      paddingRight: 10,
    },
    entryPeriod: {
      color: MUTED_COLOR,
      flexShrink: 0,
    },
    educationInstitution: {
      fontFamily: headingBold,
      flex: 1,
      paddingRight: 10,
    },
    programTitle: {
      color: theme.accentColor,
      marginBottom: 1,
    },
    originalTitle: {
      fontFamily: bodyItalic,
      color: MUTED_COLOR,
      marginBottom: 1,
    },
    entityLine: {
      marginBottom: 1,
    },
    entityName: {
      fontFamily: bodyBold,
    },
    entityMeta: {
      color: MUTED_COLOR,
    },
    detailText: {
      marginBottom: 5, // 100 twips, like the DOCX multiline paragraph
    },
    bulletRow: {
      flexDirection: "row",
      marginBottom: theme.bulletSpacing / 20, // twips -> pt
      paddingLeft: 18, // 360 twips hanging indent
    },
    bulletDot: {
      width: 18,
      marginLeft: -18,
    },
    bulletText: {
      flex: 1,
      textAlign: "justify",
    },
  });
};

export type PdfStyles = ReturnType<typeof createPdfStyles>;
